import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Send, Smartphone, Search } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Pagination } from "@/components/common/Pagination";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Field } from "@/components/common/FormShell";
import { UserName } from "@/components/common/EntityName";
import { pushApi, usersApi } from "@/lib/api";
import type { PushLog, PushResult, User } from "@/lib/api/types";
import { fmtRelativeTime } from "@/lib/utils";
import { usePagedQuery, PAGE_SIZE } from "@/lib/usePagedQuery";

type Audience = "ALL" | "CUSTOMER" | "DOCTOR" | "USER";

const AUDIENCES: { value: Audience; label: string; hint: string }[] = [
  { value: "ALL", label: "Everyone", hint: "Every device that has the app installed" },
  { value: "CUSTOMER", label: "Customers", hint: "All active patient accounts" },
  { value: "DOCTOR", label: "Doctors", hint: "All active doctor accounts" },
  { value: "USER", label: "One user", hint: "Pick a single account" },
];

/** Compose and send a push notification; the log below shows what went out. */
export default function PushPage() {
  const qc = useQueryClient();
  const [audience, setAudience] = useState<Audience>("ALL");
  const [user, setUser] = useState<User | null>(null);
  const [userQuery, setUserQuery] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [deepLink, setDeepLink] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState<PushResult | null>(null);

  const devices = useQuery({ queryKey: ["devices-count"], queryFn: () => pushApi.deviceCount().then((r) => r.data.data?.count ?? 0) });
  const people = useQuery({
    queryKey: ["users", "push-pick", userQuery],
    queryFn: () => usersApi.list({ q: userQuery, page: 0, size: 6 }).then((r) => r.data.data?.items ?? []),
    enabled: audience === "USER" && userQuery.trim().length >= 2,
  });
  const history = usePagedQuery<PushLog>({ key: ["push-history"], fetcher: pushApi.history });

  const send = useMutation({
    mutationFn: async () => {
      const base = { title: title.trim(), body: body.trim(), deepLink: deepLink.trim() || undefined };
      let payload: Parameters<typeof pushApi.send>[0] = base;
      if (audience === "USER") payload = { ...base, userId: user!.id };
      if (audience === "CUSTOMER" || audience === "DOCTOR") {
        // The push service only knows device tokens; ids by role come from iam.
        const ids = (await usersApi.idsByRole(audience)).data.data ?? [];
        if (ids.length === 0) throw new Error(`There are no active ${audience === "DOCTOR" ? "doctors" : "customers"} to notify.`);
        payload = { ...base, userIds: ids };
      }
      const res = await pushApi.send(payload);
      if (!res.data.data) throw new Error(res.data.errors?.[0]?.message ?? `The server refused this (${res.data.code}).`);
      return res.data.data;
    },
    onSuccess: (r) => {
      setResult(r);
      setConfirm(false);
      setTitle(""); setBody(""); setDeepLink("");
      qc.invalidateQueries({ queryKey: ["push-history"] });
    },
    onError: () => setConfirm(false),
  });

  const valid = title.trim().length > 0 && body.trim().length > 0 && (audience !== "USER" || !!user);

  return (
    <>
      <PageHeader
        title="Push notifications"
        subtitle={devices.data != null ? `${devices.data} devices are registered for push.` : "Send a message to the app."}
      />

      <div className="grid gap-4 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><BellRing className="size-4 text-primary" /> New notification</CardTitle>
            <CardDescription>Delivered through Firebase to the phones that opted in.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label="Audience">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {AUDIENCES.map((a) => (
                  <button
                    key={a.value}
                    type="button"
                    onClick={() => setAudience(a.value)}
                    className={`rounded-xl border p-3 text-left transition ${
                      audience === a.value ? "border-primary bg-primary/10" : "border-border hover:bg-muted"
                    }`}
                  >
                    <p className="text-sm font-semibold">{a.label}</p>
                    <p className="text-xs text-muted-foreground">{a.hint}</p>
                  </button>
                ))}
              </div>
            </Field>

            {audience === "USER" && (
              <Field label="User">
                {user ? (
                  <div className="flex items-center justify-between rounded-lg border border-border p-3">
                    <span className="text-sm font-semibold">{user.name || user.email}</span>
                    <Button size="sm" variant="outline" onClick={() => setUser(null)}>Change</Button>
                  </div>
                ) : (
                  <div>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                      <Input className="pl-9" value={userQuery} onChange={(e) => setUserQuery(e.target.value)} placeholder="Search by name or email…" />
                    </div>
                    {(people.data?.length ?? 0) > 0 && (
                      <div className="mt-2 divide-y divide-border rounded-lg border border-border">
                        {people.data!.map((u) => (
                          <button key={u.id} type="button" onClick={() => setUser(u)} className="flex w-full items-center justify-between p-2.5 text-left text-sm hover:bg-muted">
                            <span className="font-medium">{u.name || u.email}</span>
                            <Badge variant="muted">{u.role}</Badge>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Field>
            )}

            <Field label="Title">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Free consultations this weekend" maxLength={120} />
            </Field>
            <Field label="Message">
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={4}
                maxLength={500}
                className="w-full rounded-lg border border-border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-primary/40"
              />
            </Field>
            <Field label="Opens (optional deep link)">
              <Input value={deepLink} onChange={(e) => setDeepLink(e.target.value)} placeholder="/doctors or /packages" />
            </Field>

            {send.error && <p className="rounded-lg bg-destructive/10 p-2 text-sm text-destructive">{(send.error as Error).message}</p>}
            {result && (
              <div className="rounded-lg bg-success/10 p-3 text-sm text-success">
                Sent to {result.sent} device{result.sent === 1 ? "" : "s"}
                {result.failed > 0 && `, ${result.failed} failed`}
                {result.targetUsers != null && ` (${result.targetUsers} user${result.targetUsers === 1 ? "" : "s"} targeted)`}.
              </div>
            )}

            <Button className="w-full" disabled={!valid || send.isPending} onClick={() => setConfirm(true)}>
              <Send className="size-4" /> Send notification
            </Button>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Smartphone className="size-4 text-primary" /> Preview</CardTitle>
            <CardDescription>How it appears on a phone.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-2xl border border-border bg-muted/40 p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="flex size-5 items-center justify-center rounded-md bg-primary text-[10px] font-bold text-primary-foreground">A</span>
                Afya24 · now
              </div>
              <p className="mt-2 text-sm font-semibold">{title || "Title"}</p>
              <p className="text-sm text-muted-foreground">{body || "Your message appears here."}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Sent notifications</CardTitle>
          <CardDescription>{history.totalItems} sent so far.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {history.items.length === 0 ? (
            <EmptyState icon={BellRing} title={history.isFetching ? "Loading…" : "Nothing sent yet"} description="Notifications you send appear here." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Audience</TableHead>
                  <TableHead>Delivered</TableHead>
                  <TableHead>Sent by</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.items.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="text-xs text-muted-foreground">{fmtRelativeTime(p.createdAt)}</TableCell>
                    <TableCell>
                      <p className="text-sm font-semibold">{p.title}</p>
                      <p className="max-w-md truncate text-xs text-muted-foreground">{p.body}</p>
                    </TableCell>
                    <TableCell><Badge variant="muted">{p.audience === "ALL" ? "Everyone" : p.audience === "USER" ? "One user" : `${p.targetUsers ?? "?"} users`}</Badge></TableCell>
                    <TableCell className="text-sm">
                      {p.sent} / {p.requested}{p.failed > 0 && <span className="text-destructive"> ({p.failed} failed)</span>}
                    </TableCell>
                    <TableCell className="text-sm"><UserName id={p.sentBy} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Pagination page={history.page} size={PAGE_SIZE} totalItems={history.totalItems} totalPages={history.totalPages} onChange={history.setPage} />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        onConfirm={() => send.mutate()}
        icon={Send}
        title="Send this notification?"
        description={
          audience === "ALL" ? `It goes to every registered device (${devices.data ?? "…"}).`
          : audience === "USER" ? `It goes to ${user?.name || user?.email}.`
          : `It goes to every active ${audience === "DOCTOR" ? "doctor" : "customer"}.`
        }
        confirmLabel="Send"
        loading={send.isPending}
      />
    </>
  );
}
